<template>
    <div>
        <b-modal
            v-model="modalShow"
            ref="ReservationCancelModal"
            size="lg"
            :title="`${$locale.consultation_host}をキャンセル`"
            :hide-footer="true"
            @hidden="close">
            <div v-if="consultation !== null && (estimate !== null || $locale.type !== 'consul')">
                <div class="card mb-3">
                    <div class="card-header font-weight-bold">予約内容</div>
                    <div class="card-body">
                        <dl v-if="consultation.doctor !== null">
                            <dt>医師</dt>
                            <dd>
                                <span>{{ consultation.doctor.full_name }}</span>
                                <span class="small">（{{ consultation.doctor.full_name_kana }}）</span>
                            </dd>
                        </dl>
                        <dl>
                            <dt>{{ `${$locale.consultation_host}科` }}</dt>
                            <dd>{{ consultation.medical_course.name }}</dd>
                        </dl>
                        <dl v-if="consultation.doctor_menu !== null">
                            <dt>メニュー</dt>
                            <dd>{{ consultation.doctor_menu.title }}</dd>
                        </dl>
                        <dl>
                            <dt>予約日時</dt>
                            <dd>
                                <span>{{ helper.dateForHuman(consultation.start_time, consultation.user.timezone) }}</span>
                                <span
                                    class="small font-weight-bold"
                                    :class="helper.dayForHumanCSS(consultation.start_time, consultation.user.timezone)">
                                    {{ helper.dayForHuman(consultation.start_time, consultation.user.timezone, { bracket: true }) }}
                                </span>
                                <span>{{ helper.timeForHuman(consultation, consultation.user.timezone) }}</span>
                                <timezone-box-jst
                                    v-if="consultation.user.timezone !== 'Asia/Tokyo'"
                                    :consultation="consultation"
                                    :timezone="consultation.user.timezone">
                                </timezone-box-jst>
                            </dd>
                        </dl>
                        <template v-if="isPayPrice">
                            <dl>
                                <dt>お支払い方法</dt>
                                <dd>{{ consultation.payment_method.name }}</dd>
                            </dl>
                            <dl>
                                <dt>{{ $locale.type === 'consul' ? '金額（税込）' : '金額' }}</dt>
                                <template v-if="$locale.type === 'consul'">
                                    <span>{{ `${estimate.total.toLocaleString()}円` }}</span>
                                </template>
                                <template v-else>
                                    <span>{{ `${ $locale.consultation_guest }終了後に確定` }}</span>
                                </template>
                            </dl>
                        </template>
                    </div>
                </div>

                <template v-if="consultation.user && helper.isDoctorPass(consultation.user.plan)">
                    <div
                        v-if="$store.state.configs.consultation_cancel_policy_doctor_pass !== null
                        && $store.state.configs.consultation_cancel_policy_doctor_pass !== ''"
                        class="card mb-3">
                        <div class="card-header">予約のキャンセルについて</div>
                        <div class="card-body">
                            <div
                                class="vc-notice vc-notice--pre-wrap"
                                v-html="$store.state.configs.consultation_cancel_policy_doctor_pass">
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else-if="consultation.user && helper.isFlatRatePlan(consultation.user.plan)">
                    <div
                        v-if="$store.state.configs.consultation_cancel_policy_flat_rate !== null
                    && $store.state.configs.consultation_cancel_policy_flat_rate !== ''"
                        class="card mb-3">
                        <div class="card-header">予約のキャンセルについて</div>
                        <div class="card-body">
                            <div
                                class="vc-notice vc-notice--pre-wrap"
                                v-html="$store.state.configs.consultation_cancel_policy_flat_rate">
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div
                        v-if="$store.state.configs.consultation_cancel_policy !== null
                        && $store.state.configs.consultation_cancel_policy !== ''"
                        class="card mb-3">
                        <div class="card-header">予約のキャンセルについて</div>
                        <div class="card-body">
                            <div
                                class="vc-notice vc-notice--pre-wrap"
                                v-html="$store.state.configs.consultation_cancel_policy">
                            </div>
                        </div>
                    </div>
                </template>

                <div
                    v-if="$store.state.configs.subscription == 1"
                    class="card mb-3">
                    <div class="card-header font-weight-bold">キャンセル事由</div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col">
                                <div>
                                    <textarea
                                        v-model="cancel_reason"
                                        type="text"
                                        id="email"
                                        class="form-control"
                                        :class="{ 'is-invalid' : errors.cancel_reason }"
                                        placeholder="300文字以内でご入力ください（任意）">
                                    </textarea>
                                    <div v-if="errors.cancel_reason"
                                         class="invalid-feedback d-block">
                                        {{ errors.cancel_reason[0] }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <hr>

                <div class="row">
                    <div class="col">
                        <div class="text-center">
                            <p>上記の{{ $locale.consultation_host }}予約をキャンセルします。<br>よろしいですか？</p>
                            <div class="d-flex justify-content-center">
                                <button class="btn btn-yokumiru-gray mr-2" :disabled="inProgress" @click="close">閉じる</button>
                                <button class="btn btn-yokumiru-orange" :disabled="inProgress" @click="handleOk">キャンセルする</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-else
                class="d-flex justify-content-center align-items-center p-5">
                <b-spinner variant="yokumiru-user"></b-spinner>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import YokumiruHelper from '../../../YokumiruHelper'
    import constants from '../../../constants'

    export default {
        props: {
            user: {
                type: Object,
                required: true,
            },
        },

        data: function () {
            return {
                helper: YokumiruHelper,
                modalShow: false,
                inProgress: false,
                consultation: null,
                cancel_reason: '',
                estimate: null,
                errors: {},
            }
        },

        computed: {
            /**
             * 相談料の決済が必要かどうか
             * @return {boolean}
             */
            isPayPrice: function () {
                if (this.$locale.type !== 'consul') return false
                if (!this.consultation || this.isFreeMenu || this.isHealthPackage) return false

                return this.user.plan.is_pay_consultation_price
            },

            /**
             * 無料メニューかどうか
             * @return {boolean}
             */
            isFreeMenu: function () {
                return this.consultation
                    && this.consultation.doctor_menu
                    && this.consultation.doctor_menu.consultation_price < 1
            },

            /**
             * 健康管理パッケージのメニューかどうか
             * @return {boolean}
             */
            isHealthPackage: function () {
                return this.consultation
                    && this.consultation.doctor_menu
                    && this.consultation.doctor_menu.is_health_package
            },
        },

        created: function () {
            bus.$on('open-modal-reservation-cancel', this.open)
        },

        beforeDestroy: function () {
            bus.$off('open-modal-reservation-cancel')
        },

        methods: {
            /**
             * モーダル表示
             *
             * @param {object} consultation    予約情報
             */
            open: function (consultation) {
                this.consultation = consultation
                this.modalShow = true
                this.errors = {}
                this.fetchEstimate()
            },

            /**
             * モーダル非表示
             */
            close: function () {
                this.modalShow = false
            },

            /**
             * 金額（税込）
             *
             * @return {number|void}
             */
            fetchEstimate: function () {
                if (this.$locale.type !== 'consul') {
                    return
                }

                let endpoint = `${constants.user.consultations.endpoint.base}/estimate`
                endpoint += `?medical_course_id=${this.consultation.medical_course_id}`

                if (this.consultation.doctor_menu_id) {
                    endpoint += `&doctor_menu_id=${this.consultation.doctor_menu_id}`
                }
                if (this.consultation.user_coupon) {
                    endpoint += `&user_coupon_id=${this.consultation.user_coupon.id}`
                }

                axios.get(endpoint).then(({data}) => {
                    this.estimate = data.data
                })
            },

            /**
             * 予約ボタン押下時
             * 保存処理
             */
            handleOk: async function () {
                if (this.inProgress) return
                this.inProgress = true

                const endpoint = `${constants.user.consultations.endpoint.base}/${this.consultation.id}/cancel`
                const data = { cancel_reason: this.cancel_reason }

                await axios.put(endpoint, data)
                    .then(({data}) => {
                        bus.$emit('alert-success', `${this.$locale.consultation_host}予約をキャンセルしました。`)
                        bus.$emit('user-reservation-canceled', data.data)
                        this.close()
                    })
                    .catch(({response}) => {
                        if (response.data && response.data.hasOwnProperty('errors')) {
                            if (response.data.errors.hasOwnProperty('message')) {
                                bus.$emit('alert-danger', response.data.errors.message[0])
                            }
                            else {
                                this.errors = response.data.errors
                                bus.$emit('alert-danger')
                            }
                        }
                        else {
                            bus.$emit('alert-danger', 'システムエラーが発生しました。')
                        }
                    })

                this.inProgress = false
            },
        },
    }
</script>

<style scoped lang="scss">
    .vc-notice {
        &.vc-notice--pre-wrap {
            white-space: pre-wrap;
        }

        ul, ol {
            padding-left: 1rem;
        }

        h4 {
            font-size: 1.2rem;
        }
    }
</style>
